import { apiFetch } from '@/lib/api/client';

/**
 * 프로브 카탈로그 — 대화 안에서 <b>눌러서 조회해 보는</b> 칸 (DOC-109 §4).
 *
 * `actions.ts`·`views.ts` 와 성격이 또 다르다. 셋을 한 목록에 섞지 않는 이유는 `views.ts` 주석이
 * 적어 둔 그대로다 — "이 카드는 눌러야 하나" 가 카드마다 달라지면 확인 규칙이 흐려진다.
 *
 *   views    조회 **결과**. 누를 것이 없다
 *   actions  쓰기 **초안**. 눌러야 실행된다. 확인 한 번을 거친다
 *   probes   조회. **눌러야 실행된다.** 확인이 없다 — 되돌릴 것이 없기 때문이다
 *
 * ## 목록을 여기 적지 않는다
 *
 * 다른 둘과 달리 이 파일에는 **카탈로그가 없다.** 서버에서 받는다(`GET /api/v1/probes`).
 * 경로를 화면이 손으로 적어 두면 `DelegationScope` 와 사본이 둘로 갈리고, 한쪽만 늘어나는 날
 * 화면이 열리지 않은 경로를 그리거나 열린 경로를 못 그린다. 서버의 `ProbeCatalog` 는 기동할 때
 * 자기 줄을 화이트리스트로 검사하므로, 여기 내려오는 것은 이미 열려 있는 조회뿐이다.
 *
 * ## 에이전트는 키만 고른다
 *
 * `probe` 이벤트에 실리는 것은 **`key` 와 값들**이다. 경로가 아니다 — 경로를 지어낼 수 있으면
 * 대화에 섞여 들어온 문장 하나가 임의 호출이 된다. 모르는 키는 카드를 세우지 않는다.
 *
 * ## 호출은 세션 인증으로 나간다
 *
 * 위임 토큰이 아니다(`CitationCard` 와 같은 규칙 — 에이전트에게 통로를 주지 않는다). 그래서
 * 프로브로 볼 수 있는 것은 **그 사람이 화면에서 볼 수 있는 것과 정확히 같다.** 권한 밖이면
 * 그 엔드포인트의 `MenuGuard` 가 403 을 내고, 서버가 준 사유가 카드에 그대로 뜬다.
 */

export type ProbeParam = {
  name: string;
  /** PATH 는 경로의 `{name}` 자리를 채우고, QUERY 는 물음표 뒤에 붙는다. */
  in: 'PATH' | 'QUERY';
  required: boolean;
  hint: string;
};

export type Probe = {
  key: string;
  name: string;
  method: string;
  /** 경로 틀. `{id}` 자리는 PATH 파라미터가 채운다. */
  path: string;
  params: ProbeParam[];
};

/** 에이전트가 내려보내는 초안. 값은 사람이 고칠 수 있다 — 그래서 **초기값**이다. */
export type ProbeDraft = {
  key: string;
  values: Record<string, string>;
};

let cached: Promise<Probe[]> | null = null;

/**
 * 카탈로그를 한 번만 받아 둔다. 목록은 사람마다 다르지 않고 배포 중에 바뀌지 않는다 —
 * 대화 한 번에 카드가 여러 장 떠도 요청은 하나다.
 */
export function loadProbes(): Promise<Probe[]> {
  if (!cached) {
    cached = apiFetch<Probe[]>('/v1/probes').catch((error) => {
      // 다음 카드에서 다시 받아 보게 비운다. 한 번 실패했다고 그 세션 내내 못 그릴 이유가 없다.
      cached = null;
      throw error;
    });
  }
  return cached;
}

/** 경로 틀과 값으로 실제 경로를 만든다. PATH 는 자리를 채우고 QUERY 는 뒤에 붙는다. */
export function buildProbePath(probe: Probe, values: Record<string, string>): string {
  let path = probe.path;
  const query: string[] = [];

  for (const param of probe.params) {
    const value = (values[param.name] ?? '').trim();
    if (param.in === 'PATH') {
      // 비어 있으면 틀이 그대로 남는다 — 서버가 400 을 주고 그 사유가 카드에 뜬다.
      path = path.replace(`{${param.name}}`, encodeURIComponent(value));
    }
    else if (value) {
      query.push(`${encodeURIComponent(param.name)}=${encodeURIComponent(value)}`);
    }
  }

  return query.length ? `${path}?${query.join('&')}` : path;
}

/** 칸 옆에 보여 줄 질의 문자열(`?a=1&b=2`). 값이 없으면 빈 문자열이다. */
export function probeQueryPreview(probe: Probe, values: Record<string, string>): string {
  const path = buildProbePath(probe, values);
  const mark = path.indexOf('?');
  return mark < 0 ? '' : path.slice(mark);
}
